// src/pages/admin/Analytics.jsx
import { useState } from 'react';
import AdminLayout from '../../components/dashboard/admin/AdminLayout';
import OverviewTab from '../../components/admin/analytics/OverviewTab';
import RevenueTab from '../../components/admin/analytics/RevenueTab';
import TrafficTab from '../../components/admin/analytics/TrafficTab';
import PerformanceTab from '../../components/admin/analytics/PerformanceTab';
import {
  FaChartBar,
  FaDollarSign,
  FaGlobe,
  FaServer,
  FaDownload,
} from 'react-icons/fa';
import { MdRefresh } from 'react-icons/md';

const TABS = [
  {
    id: 'overview',
    label: 'Overview',
    icon: FaChartBar,
    description: 'Revenue snapshot, outstanding balance, and quote requests',
  },
  {
    id: 'revenue',
    label: 'Revenue',
    icon: FaDollarSign,
    description: 'Full Stripe invoice breakdown and monthly trends',
  },
  {
    id: 'traffic',
    label: 'Traffic & SEO',
    icon: FaGlobe,
    description: 'Sessions, page views, sources, and devices via GA4',
  },
  {
    id: 'performance',
    label: 'Performance',
    icon: FaServer,
    description: 'GCP, Firebase Hosting, and Functions metrics',
  },
];

const DATE_RANGES = [
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
  { value: '1y', label: '1 Year' },
];

const Analytics = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('30d');
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = () => setRefreshKey((k) => k + 1);

  const handleExport = () => {
    // Placeholder — wire up to export logic per-tab as needed
    alert('Export coming soon');
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab key={refreshKey} />;
      case 'revenue':
        return <RevenueTab key={refreshKey} />;
      case 'traffic':
        return <TrafficTab key={refreshKey} dateRange={dateRange} />;
      case 'performance':
        return <PerformanceTab key={refreshKey} />;
      default:
        return <OverviewTab key={refreshKey} />;
    }
  };

  const activeTabConfig = TABS.find((t) => t.id === activeTab);

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-5 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
                <p className="text-sm text-gray-500 mt-1">
                  {activeTabConfig?.description}
                </p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {/* Date Range — only relevant for Traffic tab */}
                {activeTab === 'traffic' && (
                  <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                    {DATE_RANGES.map((r) => (
                      <button
                        key={r.value}
                        onClick={() => setDateRange(r.value)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                          dateRange === r.value
                            ? 'bg-white shadow text-gray-900'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  onClick={handleRefresh}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  title="Refresh data"
                >
                  <MdRefresh className="w-4 h-4" />
                  Refresh
                </button>
                <button
                  onClick={handleExport}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <FaDownload className="w-4 h-4" />
                  Export
                </button>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="px-6">
            <nav className="flex gap-0 overflow-x-auto" aria-label="Analytics tabs">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      isActive
                        ? 'border-green-600 text-green-700'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {renderTab()}
      </div>
    </AdminLayout>
  );
};

export default Analytics;